<template>
  <section
    ref="rootRef" class="split-panel-rail">
    <div class="panel-frame">
      <div class="text-pane" :style="paneStyle">
        <GlyphView :rail="rail" />
        <h3 class="rail-title" v-if="title">{{ title }}</h3>
        <p class="rail-subtitle" v-if="subtitle">{{ subtitle }}</p>
      </div>

      <div :class="['item-pane', stacked ? 'stacked' : 'side-by-side']">
        <SplitPanelItem
          v-for="(item, index) of items"
          :key="itemKey(item, index)"
          :item="item"
          :id="itemId(index)"
          :tabindex="index"
          v-focus
          @up="moveUp(index)"
          @down="moveDown(index)"
          @left="moveLeft(index)"
          @right="moveRight(index)"
          @select="openItem(item)"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">

import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import { navigationService } from 'vue-spatialnavigation';
import { RailItemResponse, RailResponse } from '@/types/cms';
import { Language } from '@/enums/language';
import { useStore } from '@/composables/useStore';
import { useRailNavigation } from '@/composables/useRailNavigation';
import { useCMSItemNavigation } from '@/composables/useCMSItemNavigation';
import SplitPanelItem from '@/components/rails/SplitPanelItem.vue';
import GlyphView  from '@/components/glyphs/GlyphView.vue';
import { focusElementById } from '@/helpers/focus';

const props = defineProps<{
  rail: RailResponse;
  railIndex: number;
  railDidNavigateDown?: (railIndex: number) => void;
}>();

const { itemId, handleUp, handleDown, handleLeftFallback } = useRailNavigation(props);
const { openItem } = useCMSItemNavigation();

const { useGetter } = useStore();
const currentLanguage = useGetter<string>('currentLanguage');

const localised = (en: string | null, ga: string | null) => {
  const value = currentLanguage.value === Language.Irish ? ga : en;
  return value ?? en ?? '';
};

const title = computed(() => localised(props.rail.titleEn, props.rail.titleGa));
const subtitle = computed(() => localised(props.rail.subtitleEn, props.rail.subtitleGa));

const items = computed(() => props.rail.items);

const stacked = computed(() => items.value.length > 2);

const paneStyle = computed(() => ({
  'background-color': props.rail.backgroundColor ?? undefined,
  color: props.rail.textColor ?? undefined,
}));

const itemKey = (item: RailItemResponse, index: number) => {
  switch (item.type) {
    case 'SERIES':
      return `${item.series.slug}-${index}`;
    case 'VIDEO':
      return `${item.video.vid}-${index}`;
    case 'CATEGORY':
      return `${item.category.slug}-${index}`;
    default:
      return `item-${index}`;
  }
};

const focusItem = (index: number) => {
  const focusElement = navigationService.getFocusElementById(itemId(index));
  if (!focusElement) return;

  // if (document.activeElement instanceof HTMLElement) {
  //   document.activeElement.blur();
  // }

  // focusElement.focus();
  focusElementById(focusElement.id, { scroll: true });
};

/** Within the pane along its own axis, otherwise out of the rail entirely. */
const movePrevious = (index: number) => {
  if (index > 0) {
    focusItem(index - 1);
    return;
  }
  handleUp();
};

const moveNext = (index: number) => {
  if (index < items.value.length - 1) {
    focusItem(index + 1);
    return;
  }
  handleDown();
};

const moveUp = (index: number) => (stacked.value ? movePrevious(index) : handleUp());
const moveDown = (index: number) => (stacked.value ? moveNext(index) : handleDown());

// Left out of the first item leaves the rail, matching HorizontalScroller.
const moveLeft = (index: number) => {
  if (stacked.value || index === 0) {
    handleLeftFallback();
    return;
  }
  focusItem(index - 1);
};

const moveRight = (index: number) => {
  if (stacked.value) return;
  if (index < items.value.length - 1) focusItem(index + 1);
};

const rootRef = ref<HTMLElement | null>(null);
const lazyLoad = getCurrentInstance()?.proxy?.$lazyLoad;

const scanForLazyImages = () => {
  setTimeout(() => {
    if (rootRef.value) lazyLoad?.(rootRef.value);
  }, 200);
};

onMounted(scanForLazyImages);

</script>

<style scoped lang="scss">

.panel-frame {
  position: relative;
  height: 0;
  padding-bottom: 36%;
}

.text-pane,
.item-pane {
  position: absolute;
  top: 0;
  bottom: 0;
  width: calc(50% - #{$split-rail-spacing} / 2);
}

.text-pane {
  z-index: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 40px;
  box-sizing: border-box;
  text-align: left;
  background-color: $color-background-grey;
  @include text-white;
}

.item-pane {
  right: 0;
  display: flex;
}

// Two across, sharing the gap between them.
.side-by-side {
  flex-direction: row;

  > * {
    width: calc(50% - #{$split-rail-spacing} / 2);
  }

  > * + * {
    margin-left: $split-rail-spacing;
  }
}


.stacked {
  flex-direction: column;

  > * {
    flex: 1;
    min-height: 0;
  }
  > * + * {
    margin-top: $split-rail-spacing;
  }
}

.rail-title {
  margin: 0;
  @include text-title;
}

.rail-subtitle {
  margin: 10px 0 0 0;
  @include text-rail-subtitle;
}
</style>
